/**
 * Created by WorkAxis www.workaxis.in
 * Developer: Padma Dev.E
 * Developer Contact: padmadev.e@workaxis.in
 * Admin: shyam@workaxis.in
 * Date: 3/14/2022
 * Time: 9:08 PM
 * Project: w_crm
 */
import { createSlice } from "@reduxjs/toolkit";
import { eraseCookie, getCookie, setCookie } from "../../utils/cookies";
import { ACCOUNTS_COOKIE_KEY, ACTIVE_ACCOUNT_COOKIE_KEY } from "../../utils/constants";
import jwt from "jwt-decode";
import apiService from "../../api/apiService";
import { endPoints } from "../../api/endpoints";
import { formatAxiosErrorMessage, isJwtValid } from "../../utils/common";
import { hardRefreshPage } from "../profile/profileReducer";
import { clearLocalStorage } from "../../utils/localStorage";

const initialAuthState = {
    //States used as common for all api calls in Credential Section
    isLoading: false,
    error: null,
    isProcessed: false,

    ipDetails: null,

    isAuthenticated: false,
    notice: {
        type: null,
        message: null,
        isOn: false,
    },

    changePassword: {
        isLoading: false,
        data: null,
        error: null,
    },
    changeEmail: {
        isLoading: false,
        data: null,
        error: null,
    },
    verify_email: {
        isLoading: false,
        data: null,
        error: null,
    },

    resendVerificationEmail: {
        isLoading: false,
        data: null,
        error: null,
    },

};

export const authSlice = createSlice({
    name: "auth",
    initialState: {
        ...initialAuthState,
    },
    reducers: {
        login: (state, action) => {
            let response = action.payload;
            // Extract tokens - support both wrapped and unwrapped response formats
            const accessToken = response.access || response.data?.access;
            const refreshToken = response.refresh || response.data?.refresh;

            if (!accessToken) {
                console.error("System Error: No access token found in login response", response);
                state.isAuthenticated = false;
                state.isProcessed = false;
                state.isLoading = false;
                state.error = "Token extraction failed";
                return;
            }

            state.isAuthenticated = true;
            state.isProcessed = true;
            state.isLoading = false;

            console.log("Auth System: Login successful. Setting access token.", { accessToken });

            try {
                const decoded = jwt(accessToken);
                const userId = (decoded.user_id || decoded.id || decoded.email || "unknown").toString();

                let accounts = [];
                try {
                    const cookieVal = getCookie(ACCOUNTS_COOKIE_KEY);
                    if (cookieVal) {
                        const parsed = JSON.parse(cookieVal);
                        if (Array.isArray(parsed)) {
                            accounts = parsed;
                        }
                    }
                } catch (e) {
                    console.error("Failed to parse accounts cookie", e);
                }

                const newAccount = {
                    id: userId,
                    access: accessToken,
                    refresh: refreshToken,
                    email: decoded.email,
                    first_name: decoded.first_name,
                    last_name: decoded.last_name,
                    username: decoded.username,
                    profile_pic: decoded.profile_pic,
                    lastActive: new Date().getTime()
                };

                // Filter out any existing session for this same user
                accounts = accounts.filter(acc => acc.id.toString() !== userId);
                accounts.push(newAccount);

                // Save to cookies with 1 year expiry
                setCookie(ACCOUNTS_COOKIE_KEY, JSON.stringify(accounts), 365);
                setCookie(ACTIVE_ACCOUNT_COOKIE_KEY, userId, 365);

                console.log(`System: Multi-account login successful for ${userId}. Total accounts: ${accounts.length}`);
            } catch (e) {
                console.error("System Error: Managing multi-account login failed during JWT decoding or cookie writing.", e);
            }
        },
        refresh: (state, action) => {
            let response = action.payload;
            const newAccess = response.access || response.data?.access;
            const activeId = getCookie(ACTIVE_ACCOUNT_COOKIE_KEY);
            let newRefresh = response.refresh || response.data?.refresh;

            if (!newRefresh && activeId) {
                try {
                    const accounts = JSON.parse(getCookie(ACCOUNTS_COOKIE_KEY) || "[]");
                    const acc = accounts.find(a => a.id.toString() === activeId.toString());
                    if (acc) newRefresh = acc.refresh;
                } catch (e) { }
            }

            state.isAuthenticated = true;
            state.isProcessed = true;
            state.isLoading = false;

            if (newAccess && activeId) {
                try {
                    let accounts = [];
                    const cookieVal = getCookie(ACCOUNTS_COOKIE_KEY);
                    if (cookieVal) {
                        try { accounts = JSON.parse(cookieVal); } catch (e) { }
                    }

                    const updatedAccounts = accounts.map(acc => {
                        if (acc.id.toString() === activeId.toString()) {
                            return { ...acc, access: newAccess, refresh: newRefresh, lastActive: new Date().getTime() };
                        }
                        return acc;
                    });
                    setCookie(ACCOUNTS_COOKIE_KEY, JSON.stringify(updatedAccounts), 365);
                } catch (e) {
                    console.error("Error updating refreshed token in accounts list", e);
                }
            }
        },
        switchAccount: (state, action) => {
            const targetId = action.payload;
            let accounts = [];
            try {
                const cookieVal = getCookie(ACCOUNTS_COOKIE_KEY);
                if (cookieVal) { accounts = JSON.parse(cookieVal); }
            } catch (e) { }

            const targetAccount = accounts.find(acc => acc.id.toString() === targetId.toString());

            if (targetAccount) {

                setCookie(ACTIVE_ACCOUNT_COOKIE_KEY, targetAccount.id, 365);
                state.isAuthenticated = true;
                // We rely on hardRefreshPage or parent component to reload data
            }
        },
        logout: (state) => {
            try {
                const activeId = getCookie(ACTIVE_ACCOUNT_COOKIE_KEY);
                let accounts = [];
                const cookieVal = getCookie(ACCOUNTS_COOKIE_KEY);
                if (cookieVal) { accounts = JSON.parse(cookieVal); }

                if (activeId) {
                    accounts = accounts.filter(acc => acc.id.toString() !== activeId.toString());
                }

                if (accounts.length > 0) {
                    const nextAccount = accounts[accounts.length - 1];
                    setCookie(ACCOUNTS_COOKIE_KEY, JSON.stringify(accounts), 365);
                    setCookie(ACTIVE_ACCOUNT_COOKIE_KEY, nextAccount.id, 365);

                    state.isAuthenticated = true;
                } else {

                    eraseCookie(ACCOUNTS_COOKIE_KEY);
                    eraseCookie(ACTIVE_ACCOUNT_COOKIE_KEY);
                    clearLocalStorage();
                    state.isAuthenticated = false;
                }
            } catch (e) {

                clearLocalStorage();
                state.isAuthenticated = false;
            }
        },
        checkAuth: (state, action) => {
            const activeId = getCookie(ACTIVE_ACCOUNT_COOKIE_KEY);
            let token = null;
            if (activeId) {
                try {
                    const accounts = JSON.parse(getCookie(ACCOUNTS_COOKIE_KEY) || "[]");
                    const acc = accounts.find(a => a.id.toString() === activeId.toString());
                    if (acc) token = acc.access;
                } catch (e) { }
            }
            if (!token) {
                state.isAuthenticated = false;
            } else {
                state.isAuthenticated = isJwtValid(token);
            }
        },
        setAuthNotice: (state, action) => {
            state.notice.type = action.payload.type;
            state.notice.message = action.payload.message;
            state.notice.isOn = true;
        },
        requestingAuthApiCall: (state) => {
            state.isProcessed = false;
            state.isLoading = true;
            state.error = null;
        },
        authApiCallSuccess: (state) => {
            state.isProcessed = true;
            state.isLoading = false;
        },
        authApiCallError: (state, action) => {
            state.isProcessed = false;
            state.isLoading = false;
            state.error = action.payload;
        },
        resetAuthApiCall: () => initialAuthState,

        setIpDetails: (state, action) => {
            state.ipDetails = action.payload;
        },

        changePasswordRequesting: (state, action) => {
            state.changePassword.isLoading = true;
        },
        changePasswordSuccess: (state, action) => {
            state.changePassword.isLoading = false;
            state.changePassword.error = null;
            state.changePassword.data = true;
        },
        changePasswordError: (state, action) => {
            state.changePassword.isLoading = false;
            state.changePassword.error = action.payload;
        },
        changePasswordReset: (state, action) => {
            state.changePassword = initialAuthState.changePassword;
        },


        resendVerificationEmailRequesting: (state, action) => {
            state.resendVerificationEmail.isLoading = true;
        },
        resendVerificationEmailSuccess: (state, action) => {
            state.resendVerificationEmail.isLoading = false;
            state.resendVerificationEmail.error = null;
            state.resendVerificationEmail.data = true;
        },
        resendVerificationEmailError: (state, action) => {
            state.resendVerificationEmail.isLoading = false;
            state.resendVerificationEmail.error = action.payload;
        },
        resendVerificationEmailReset: (state, action) => {
            state.resendVerificationEmail = initialAuthState.resendVerificationEmail;
        },


        changeEmailRequesting: (state, action) => {
            state.changeEmail.isLoading = true;
        },
        changeEmailSuccess: (state, action) => {
            state.changeEmail.isLoading = false;
            state.changeEmail.error = null;
            state.changeEmail.data = true;
        },
        changeEmailError: (state, action) => {
            state.changeEmail.isLoading = false;
            state.changeEmail.error = action.payload;
        },
        changeEmailReset: (state, action) => {
            state.changeEmail = initialAuthState.changeEmail;
        },

        VerifyEmailRequesting: (state, action) => {
            state.verify_email.isLoading = true;
        },
        VerifyEmailSuccess: (state, action) => {
            state.verify_email.isLoading = false;
            state.verify_email.error = null;
            state.verify_email.data = true;
        },
        VerifyEmailError: (state, action) => {
            state.verify_email.isLoading = false;
            state.verify_email.error = action.payload;
        },
        VerifyEmailReset: (state, action) => {
            state.verify_email = initialAuthState.verify_email;
        },


        AuthMasterReset: () => initialAuthState,
    },
});

export const loginApi = (data) => async (dispatch) => {
    dispatch(requestingAuthApiCall());
    apiService
        .post(endPoints.auth.login, data)
        .then((response) => dispatch(login(response.data)))
        .catch((error) =>
            dispatch(authApiCallError(formatAxiosErrorMessage(error)))
        );
};

export const GoogleLoginApi = (data) => async (dispatch) => {
    dispatch(requestingAuthApiCall());
    const payload = {
        code: data.code,
        ...(data.timezone && { timezone: data.timezone })
    };
    apiService
        .post(endPoints.auth.googleLogin, payload)
        .then((response) => dispatch(login(response.data)))
        .catch((error) =>
            dispatch(authApiCallError(formatAxiosErrorMessage(error)))
        );
};

export const logoutApi = () => async (dispatch) => {
    dispatch(requestingAuthApiCall());
    apiService
        .post(endPoints.auth.logout, (() => {
            const activeId = getCookie(ACTIVE_ACCOUNT_COOKIE_KEY);
            let refresh = "";
            if (activeId) {
                try {
                    const accounts = JSON.parse(getCookie(ACCOUNTS_COOKIE_KEY) || "[]");
                    const acc = accounts.find(a => a.id.toString() === activeId.toString());
                    if (acc) refresh = acc.refresh;
                } catch (e) { }
            }
            return { "refresh": refresh };
        })(),)
        // .post(endPoints.auth.logout, {},)
        .then((response) => {
            dispatch(masterLogout())
        })
        .catch((error) => {
            dispatch(authApiCallError(formatAxiosErrorMessage(error)))
            dispatch(masterLogout())
        }
        );
};

export const refreshTokenApi = (data) => async (dispatch) => {
    dispatch(requestingAuthApiCall());
    apiService
        .post(endPoints.auth.refresh_token, (() => {
            const activeId = getCookie(ACTIVE_ACCOUNT_COOKIE_KEY);
            let refresh = "";
            if (activeId) {
                try {
                    const accounts = JSON.parse(getCookie(ACCOUNTS_COOKIE_KEY) || "[]");
                    const acc = accounts.find(a => a.id.toString() === activeId.toString());
                    if (acc) refresh = acc.refresh;
                } catch (e) { }
            }
            return { "refresh": refresh };
        })())
        .then((response) => dispatch(refresh(response.data)))
        .catch((error) => {
            dispatch(authApiCallError(formatAxiosErrorMessage(error)))
        });
};
export const registerApi = (data) => async (dispatch) => {
    dispatch(requestingAuthApiCall());
    apiService
        .post(endPoints.auth.register, data)
        .then(() => dispatch(authApiCallSuccess()))
        .catch((error) =>
            dispatch(authApiCallError(formatAxiosErrorMessage(error)))
        );
};

export const forgotPasswordApi = (data) => async (dispatch) => {
    dispatch(requestingAuthApiCall());
    apiService
        .post(endPoints.auth.forgot_password, data)
        .then((response) => dispatch(authApiCallSuccess()))
        .catch((error) =>
            dispatch(authApiCallError(formatAxiosErrorMessage(error)))
        );
};

export const verifyForgotPasswordApi = (data, uid, token) => async (dispatch) => {
    dispatch(requestingAuthApiCall());
    apiService
        .post(`${endPoints.auth.verify_forgot_password}${uid}/${token}`, data)
        .then(() => dispatch(authApiCallSuccess()))
        .catch((error) =>
            dispatch(authApiCallError(formatAxiosErrorMessage(error)))
        );
};

export const changePasswordApi = (data) => async (dispatch) => {
    dispatch(changePasswordRequesting());
    apiService
        .post(endPoints.auth.change_password, data)
        .then((response) => dispatch(changePasswordSuccess(response.data)))
        .catch((error) =>
            dispatch(changePasswordError(formatAxiosErrorMessage(error)))
        );
};

export const changeEmailApi = (data) => async (dispatch) => {
    dispatch(changeEmailRequesting());
    apiService
        .post(endPoints.auth.change_email, data)
        .then((response) => dispatch(changeEmailSuccess(response.data)))
        .catch((error) =>
            dispatch(changeEmailError(formatAxiosErrorMessage(error)))
        );
};

export const resendVerifyEmailAPI = (email) => async (dispatch) => {
    dispatch(resendVerificationEmailRequesting());
    apiService
        .post(`${endPoints.auth.resend_verify_email}`, { email: email })
        .then((response) => dispatch(resendVerificationEmailSuccess(response.data)))
        .catch((error) =>
            dispatch(resendVerificationEmailError(formatAxiosErrorMessage(error)))
        );
};

export const verifyEmailAPI = (uid) => async (dispatch) => {
    dispatch(VerifyEmailRequesting());
    apiService
        .post(`${endPoints.auth.verify_email}`, { key: uid })
        .then((response) => dispatch(VerifyEmailSuccess(response.data)))
        .catch((error) =>
            dispatch(VerifyEmailError(formatAxiosErrorMessage(error)))
        );
};

export const masterLogout = () => async (dispatch) => {
    dispatch(logout());
    dispatch(hardRefreshPage());
};

export const {
    login,
    refresh,
    checkAuth,
    setAuthNotice,
    logout,
    requestingAuthApiCall,
    authApiCallSuccess,
    authApiCallError,
    resetAuthApiCall,
    setIpDetails,

    changePasswordRequesting,
    changePasswordSuccess,
    changePasswordError,
    changePasswordReset,

    AuthMasterReset,

    VerifyEmailRequesting,
    VerifyEmailSuccess,
    VerifyEmailError,
    VerifyEmailReset,


    changeEmailRequesting,
    changeEmailSuccess,
    changeEmailError,
    changeEmailReset,


    resendVerificationEmailRequesting,
    resendVerificationEmailSuccess,
    resendVerificationEmailError,
    resendVerificationEmailReset,

    switchAccount,
} = authSlice.actions;

export const refreshAllAccounts = () => async (dispatch) => {
    try {
        const cookieVal = getCookie(ACCOUNTS_COOKIE_KEY);
        if (!cookieVal) return;
        const accounts = JSON.parse(cookieVal);
        let updated = false;

        const newAccounts = await Promise.all(accounts.map(async (acc) => {
            // Check if access token is expired ? 
            // We can just eagerly refresh or check expiry. 
            // Ideally we check expiry to save calls.
            const decoded = jwt(acc.access);
            const now = new Date().getTime() / 1000;
            if (decoded.exp - now < 300) { // If less than 5 mins
                try {
                    const response = await apiService.post(endPoints.auth.refresh_token, { "refresh": acc.refresh });
                    updated = true;
                    // axios response.data contains the body
                    const body = response.data;
                    const newAccess = body.access || body.data?.access;
                    const newRefresh = body.refresh || body.data?.refresh || acc.refresh;

                    return { ...acc, access: newAccess, refresh: newRefresh };
                } catch (err) {
                    console.error(`Failed to refresh for ${acc.email}`, err);
                    return acc;
                }
            }
            return acc;
        }));

        if (updated) {
            // Re-read cookie to avoid overwriting changes (e.g. login/logout) that happened during await
            const currentCookieVal = getCookie(ACCOUNTS_COOKIE_KEY);
            const currentAccounts = currentCookieVal ? JSON.parse(currentCookieVal) : [];

            const mergedAccounts = currentAccounts.map(curr => {
                const refreshed = newAccounts.find(n => n.id.toString() === curr.id.toString());
                if (refreshed) {
                    return { ...curr, access: refreshed.access, refresh: refreshed.refresh };
                }
                return curr;
            });

            setCookie(ACCOUNTS_COOKIE_KEY, JSON.stringify(mergedAccounts), 365);
        }
    } catch (e) {
        console.error("Error refreshing all accounts", e);
    }
};
export const authState = (state) => state.auth;
export default authSlice.reducer;
